import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Camera, 
  DollarSign, 
  Loader2,
  Search,
  ListChecks,
} from 'lucide-react';
import { useMyServices, useCreateVendorService, useUpdateVendorService, useDeleteVendorService, useToggleServiceStatus } from '../../../services/vendorServiceService';
import { useToastContext } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/shared/Button';
import Input from '../../../components/shared/Input';
import SearchSelect from '../../../components/shared/SearchSelect';
import { ServiceModal } from '../../../components/vendor';
import { useVendorProfile } from '../../../services/vendorProfileService';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';
import { VendorPageHeader, VendorGuideCallout } from '../../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../../utils/vendorPageGuides';

const parseSpecialties = (specialties) => {
  if (!specialties) return [];
  let list = specialties;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      return list.trim() ? [list.trim()] : [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') return String(item.name || item.label || '').trim();
      return '';
    })
    .filter(Boolean);
};

const VendorServices = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [modalState, setModalState] = useState({
    isOpen: false,
    isEdit: false,
    selectedService: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    service_name: '',
    image_url: '',
    description: '',
    search_tags: '',
    service_category_id: '',
    base_price: '',
    is_featured: false,
    sort_order: 0
  });
// console.log('====================================');
// console.log(formData);
// console.log('====================================');
  // Fetch data
  const { data: servicesData, isLoading: servicesLoading, refetch } = useMyServices();
  const { data: vendorProfileResponse, isLoading: profileLoading } = useVendorProfile();
  const { data: statusResponse } = useVendorSubscriptionStatus();

  // Mutations
  const createServiceMutation = useCreateVendorService({
    onSuccess: () => {
      success('Service created successfully!');
      closeModal();
      resetForm();
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to create service');
    }
  });

  const updateServiceMutation = useUpdateVendorService({
    onSuccess: () => {
      success('Service updated successfully!');
      closeModal();
      resetForm();
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update service');
    }
  });

  const deleteServiceMutation = useDeleteVendorService({
    onSuccess: () => {
      success('Service deleted successfully!');
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to delete service');
    }
  });

  const toggleStatusMutation = useToggleServiceStatus({
    onSuccess: () => {
      success('Service status updated successfully!');
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update service status');
    }
  });

  // Handle paginated response from API
  const services = Array.isArray(servicesData?.data?.data) ? servicesData.data.data : [];
  
  // Get categories from vendor profile (selected during verification)
  const categories = user?.vendor_profile?.categories || vendorProfileResponse?.data?.categories || [];
  const vendorSpecialties = parseSpecialties(
    vendorProfileResponse?.data?.specialties ?? user?.vendor_profile?.specialties
  );
  const usageLimits = statusResponse?.data?.current_subscription?.limits || {};
  const usage = statusResponse?.data?.current_subscription?.usage || {};
  const serviceLimit = usageLimits?.max_services ?? null;
  const currentServiceCount = usage?.services_count ?? services.length;
  const atServiceLimit = Boolean(statusResponse?.data?.current_subscription?.at_service_limit);
  const imageLimit = usageLimits?.max_images ?? null;
  const currentImageCount = usage?.images_count ?? 0;

  // Filter services
  const filteredServices = services.filter(service => {
    const matchesSearch = service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || service.service_category_id === selectedCategory;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && service.is_active) ||
                         (statusFilter === 'inactive' && !service.is_active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const resetForm = () => {
    setFormData({
      service_name: '',
      image_url: '',
      description: '',
      search_tags: '',
      service_category_id: '',
      base_price: '',
      is_featured: false,
      sort_order: 0
    });
  };

  const openCreateModal = () => {
    setModalState({ isOpen: true, isEdit: false, selectedService: null });
    resetForm();
  };

  const openEditModal = (service) => {
    const allowedSet = new Set(vendorSpecialties);
    const selectedTags = (service.search_tags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
      .filter((tag) => allowedSet.has(tag));

    setModalState({ isOpen: true, isEdit: true, selectedService: service });
    setFormData({
      service_name: service.service_name || '',
      image_url: service.image_url || '',
      description: service.description || '',
      search_tags: selectedTags.join(', '),
      service_category_id: service.service_category_id || '',
      base_price: service.base_price || '',
      is_featured: service.is_featured || false,
      sort_order: service.sort_order || 0
    });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, isEdit: false, selectedService: null });
    resetForm();
  };

  const handleCreate = () => {
    if (categories.length === 0) {
      showError('You need to select service categories during verification before creating services. Please complete your verification process.');
      return;
    }
    openCreateModal();
  };

  const handleEdit = (service) => {
    openEditModal(service);
  };

  const handleDelete = (service) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      deleteServiceMutation.mutate(service.id);
    }
  };

  const handleToggleStatus = (service) => {
    toggleStatusMutation.mutate(service.id);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalState.isEdit) {
      updateServiceMutation.mutate({ id: modalState.selectedService.id, data: formData });
    } else {
      createServiceMutation.mutate(formData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageSelect = (image) => {
    if (image) {
      handleInputChange('image_url', image.image_url);
    }
  };


  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.category_name || 'Unknown Category';
  };

  if (servicesLoading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-ink" />
          <p className="text-ink-secondary">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
        <VendorPageHeader
          title="Services"
          subtitle={
            <>
              {categories.length === 0
                ? 'Complete verification to select service categories and start creating services'
                : 'Manage your service offerings'}
              <span className="mt-1 block text-ink-tertiary">
                Services: {currentServiceCount}
                {serviceLimit === null ? ' / unlimited' : ` / ${serviceLimit}`} | Images:{' '}
                {currentImageCount}
                {imageLimit === null ? ' / unlimited' : ` / ${imageLimit}`}
              </span>
            </>
          }
          actions={
            <Button
              onClick={handleCreate}
              icon={Plus}
              className="w-full sm:w-auto"
              disabled={categories.length === 0 || atServiceLimit}
              title={
                categories.length === 0
                  ? 'Complete verification to select service categories first'
                  : atServiceLimit
                    ? "You have reached your plan's service limit"
                    : ''
              }
            >
              Add New Service
            </Button>
          }
        />

        <VendorGuideCallout
          title={VENDOR_PAGE_GUIDES.services.title}
          steps={VENDOR_PAGE_GUIDES.services.steps}
          icon={ListChecks}
        />

        {categories.length === 0 && (
          <p className="text-sm text-amber-600 -mt-2 mb-4">
            You need to complete verification and select service categories first
          </p>
        )}
        {categories.length > 0 && atServiceLimit && (
          <p className="text-sm text-amber-600 -mt-2 mb-4">
            You have reached your service limit for the current plan. Upgrade to add more services.
          </p>
        )}

        <div className="product-filter-panel mb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-ink">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary" />
                <Input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="mb-2 block text-sm font-medium text-ink">Category</label>
              <SearchSelect
                value={selectedCategory}
                onChange={setSelectedCategory}
                options={[
                  { value: '', label: 'All Categories' },
                  ...categories.map(category => ({
                    value: category.id,
                    label: category.category_name
                  }))
                ]}
                placeholder="Filter by category"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-sm font-medium text-ink">Status</label>
              <SearchSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Filter by status"
              />
            </div>
          </div>
        </div>

        {filteredServices.length === 0 ? (
          <div className="product-surface p-12 text-center">
            <Camera className="mx-auto mb-4 h-16 w-16 text-ink-tertiary" />
            <h3 className="mb-2 text-lg font-semibold text-ink">No services found</h3>
            <p className="mb-6 text-ink-secondary">
              {services.length === 0 
                ? categories.length === 0
                  ? "You need to select service categories during verification before creating services."
                  : "You haven't created any services yet. Get started by adding your first service!"
                : "No services match your current filters. Try adjusting your search criteria."
              }
            </p>
            {services.length === 0 && categories.length > 0 && (
              <Button onClick={handleCreate} icon={Plus} disabled={atServiceLimit}>
                Create Your First Service
              </Button>
            )}
            {services.length === 0 && categories.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-ink-tertiary">
                  Please complete your vendor verification and select service categories first.
                </p>
                <Button 
                  onClick={() => window.location.href = '/vendor/verification'} 
                  variant="outline"
                >
                  Complete Verification
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filteredServices.map((service) => (
              <div key={service.id} className="product-panel">
                <div className="h-48 bg-surface-muted">
                  {service.image_url ? (
                    <img
                      src={service.image_url}
                      alt={service.service_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Camera className="h-12 w-12 text-ink-tertiary" />
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <h3 className="truncate text-lg font-semibold text-ink">
                      {service.service_name}
                    </h3>
                    <span className={`rounded-product-sm px-2 py-1 text-xs font-medium ${ service.is_active ? 'bg-surface-muted text-ink' : 'bg-surface-muted text-ink-tertiary' }`}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="mb-3 line-clamp-2 text-sm text-ink-secondary">
                    {service.description}
                  </p>
                  
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center text-sm text-ink-secondary">
                      <span className="mr-2 font-medium text-ink">Category:</span>
                      <span className="product-chip">
                        {getCategoryName(service.service_category_id)}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-ink">
                      <DollarSign className="mr-2 h-4 w-4 text-ink-tertiary" />
                      <span className="font-medium">${service.base_price}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEdit(service)}
                        variant="outline"
                        size="sm"
                        icon={Edit}
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleToggleStatus(service)}
                        variant="outline"
                        size="sm"
                        icon={service.is_active ? EyeOff : Eye}
                      >
                        {service.is_active ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    
                    <Button
                      onClick={() => handleDelete(service)}
                      variant="outline"
                      size="sm"
                      icon={Trash2}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Service Modal */}
        <ServiceModal
          isOpen={modalState.isOpen}
          isEdit={modalState.isEdit}
          formData={formData}
          categories={categories}
          specialties={vendorSpecialties}
          specialtiesLoading={profileLoading && vendorSpecialties.length === 0}
          onClose={closeModal}
          onSubmit={handleSubmit}
          onInputChange={handleInputChange}
          onImageSelect={handleImageSelect}
          maxImages={imageLimit}
          aiAccess={{
            enabled: Boolean(statusResponse?.data?.current_subscription?.features?.feature_ai_generator),
            limit: statusResponse?.data?.current_subscription?.limits?.max_ai_generations_per_month ?? null,
            used: statusResponse?.data?.current_subscription?.usage?.ai_generations_this_month ?? 0,
          }}
          isLoading={createServiceMutation.isPending || updateServiceMutation.isPending}
        />
    </div>
  );
};

export default VendorServices;
